import { Dispatch, Reducer } from 'react';
import { Campaign, RepairOrder } from '@packages/models/api';
import { action, on, props, reducer, union } from 'ts-action';

export enum DmsActionsList {
    SET_LATEST_ORDER = 'GET_LATEST_ORDER',
    SET_LATEST_OPEN_CAMPAIGN = 'SET_LATEST_OPEN_CAMPAIGN',

    SET_REPAIR_ORDERS_HISTORY = 'GET_REPAIR_ORDERS_HISTORY',
    SET_CAMPAIGNS_BY_VIN = 'SET_CAMPAIGNS_BY_VIN',

    SET_SELECTED_ORDER = 'SET_SELECTED_ORDER',

    HAS_LOADED = 'HAS_LOADED',
    HAS_LATEST_CAMPAIGN_FETCHED = 'HAS_LATEST_CAMPAIGN_FETCHED',

    IS_LOADING = 'IS_LOADING',
    IS_CAMPAIGN_LOADING = 'IS_CAMPAIGN_LOADING',
    IS_LATEST_CAMPAIGN_LOADING = 'IS_LATEST_CAMPAIGN_LOADING',

    HAS_FETCHED = 'HAS_FETCHED',

    HAS_HISTORY_FETCHED = 'HAS_HISTORY_FETCHED',
    HAS_CAMPAIGN_HISTORY_FETCHED = 'HAS_CAMPAIGN_HISTORY_FETCHED',

    SET_CAMPAIGN_COUNT = 'SET_CAMPAIGN_COUNT',
    SET_DMS_COUNT = 'SET_DMS_COUNT',
    SET_OPEN_CAMPAIGN_COUNT = 'SET_OPEN_CAMPAIGN_COUNT',

    CLEAR_DMS_DATA = 'CLEAR_DMS_DATA',

    SET_SORT = 'SET_SORT',
}

export const dmsActions = {
    setRepairOrdersHistory: action(
        DmsActionsList.SET_REPAIR_ORDERS_HISTORY,
        props<{ repairOrders: RepairOrder[] | [] }>()
    ),
    setLatestOrder: action(DmsActionsList.SET_LATEST_ORDER, props<{ latestOrder: RepairOrder | null }>()),
    setSelectedOrder: action(DmsActionsList.SET_SELECTED_ORDER, props<{ selectedOrder: RepairOrder | null }>()),
    hasLoaded: action(DmsActionsList.HAS_LOADED),
    isLoading: action(DmsActionsList.IS_LOADING),
    clearDmsData: action(DmsActionsList.CLEAR_DMS_DATA),
    setDmsCount: action(DmsActionsList.SET_DMS_COUNT),
    hasFetched: action(DmsActionsList.HAS_FETCHED),
    hasHistoryFetched: action(DmsActionsList.HAS_HISTORY_FETCHED),
    setLatestOpenCampaign: action(
        DmsActionsList.SET_LATEST_OPEN_CAMPAIGN,
        props<{ latestOpenCampaign: Campaign | null }>()
    ),
    setCampaignsByVin: action(DmsActionsList.SET_CAMPAIGNS_BY_VIN, props<{ campaignsByVin: any[] | [] }>()),
    hasLatestCampaignFetched: action(DmsActionsList.HAS_LATEST_CAMPAIGN_FETCHED),
    hasCampaignHistoryFetched: action(DmsActionsList.HAS_CAMPAIGN_HISTORY_FETCHED),
    isCampaignLoading: action(DmsActionsList.IS_CAMPAIGN_LOADING, props<{ isLoading: boolean }>()),
    isLatestCampaignLoading: action(DmsActionsList.IS_LATEST_CAMPAIGN_LOADING, props<{ isLoading: boolean }>()),
    setCampaignCount: action(DmsActionsList.SET_CAMPAIGN_COUNT),
    setOpenCampaignCount: action(DmsActionsList.SET_OPEN_CAMPAIGN_COUNT, props<{ openCampaignCount: number }>()),
    setSort: action(DmsActionsList.SET_SORT, props<{ sortBy: string; sortDirection: 'ASC' | 'DESC' }>()),
};

const actionsUnion = union(...Object.values(dmsActions));
type DmsActions = typeof actionsUnion.actions;
export type DmsActionDispatcher = Dispatch<DmsActions>;

export interface DmsState {
    repairOrdersHistory: RepairOrder[];
    latestRepairOrder: RepairOrder | null;
    selectedRepairOrder: RepairOrder | null;
    loading: boolean;
    dmsCount: number;
    hasFetched: boolean;
    hasHistoryFetched: boolean;
    campaignCount: number;
    openCampaignCount: number;
    latestOpenCampaign: Campaign | null;
    campaignsByVin: Campaign[];
    hasLatestCampaignFetched: boolean;
    hasCampaignHistoryFetched: boolean;
    isCampaignLoading: boolean;
    isLatestCampaignLoading: boolean;
    sortState: {
        roTable: { sortBy: string | null; sortDirection: 'ASC' | 'DESC' | null };
    };
}

export const initialState: DmsState = {
    repairOrdersHistory: [],
    latestRepairOrder: null,
    selectedRepairOrder: null,
    loading: false,
    dmsCount: 0,
    hasFetched: false,
    hasHistoryFetched: false,
    campaignCount: 0,
    openCampaignCount: 0,
    latestOpenCampaign: null,
    campaignsByVin: [],
    hasLatestCampaignFetched: false,
    hasCampaignHistoryFetched: false,
    isCampaignLoading: false,
    isLatestCampaignLoading: false,
    sortState: {
        roTable: { sortBy: null, sortDirection: null },
    },
};

export const dmsReducer: Reducer<DmsState, DmsActions> = reducer(
    initialState,
    on(dmsActions.setRepairOrdersHistory, (state, { repairOrders }) => {
        return {
            ...state,
            repairOrdersHistory: repairOrders,
        };
    }),
    on(dmsActions.setLatestOrder, (state, { latestOrder }) => {
        return {
            ...state,
            latestRepairOrder: latestOrder,
        };
    }),
    on(dmsActions.setSelectedOrder, (state, { selectedOrder }) => {
        return {
            ...state,
            selectedRepairOrder: selectedOrder,
        };
    }),
    on(dmsActions.isLoading, (state) => {
        return {
            ...state,
            loading: true,
        };
    }),
    on(dmsActions.hasLoaded, (state) => {
        return {
            ...state,
            loading: false,
        };
    }),
    on(dmsActions.clearDmsData, (state) => {
        return {
            ...state,
            repairOrdersHistory: [],
            latestRepairOrder: null,
            dmsCount: 0,
            hasFetched: false,
            hasHistoryFetched: false,
            loading: false,
            selectedRepairOrder: null,
            campaignCount: 0,
            openCampaignCount: 0,
            latestOpenCampaign: null,
            campaignsByVin: [],
            hasLatestCampaignFetched: false,
            hasCampaignHistoryFetched: false,
            isCampaignLoading: false,
            isLatestCampaignLoading: false,
            sortState: {
                ...state.sortState,
                roTable: { sortBy: null, sortDirection: null },
            },
        };
    }),
    on(dmsActions.setDmsCount, (state) => {
        if (!state.repairOrdersHistory || !state.repairOrdersHistory.length) {
            return {
                ...state,
                dmsCount: 0,
            };
        }
        return {
            ...state,
            dmsCount: state.repairOrdersHistory.length,
        };
    }),
    on(dmsActions.hasFetched, (state) => {
        return {
            ...state,
            hasFetched: true,
        };
    }),
    on(dmsActions.hasHistoryFetched, (state) => {
        return {
            ...state,
            hasHistoryFetched: true,
        };
    }),
    on(dmsActions.setCampaignCount, (state) => {
        if (!state.campaignsByVin || !state.campaignsByVin.length) {
            return {
                ...state,
                campaignCount: 0,
            };
        }
        return {
            ...state,
            campaignCount: state.campaignsByVin.length,
        };
    }),
    on(dmsActions.setOpenCampaignCount, (state, { openCampaignCount }) => {
        return {
            ...state,
            openCampaignCount: openCampaignCount,
        };
    }),
    on(dmsActions.setLatestOpenCampaign, (state, { latestOpenCampaign }) => {
        return {
            ...state,
            latestOpenCampaign: latestOpenCampaign,
        };
    }),
    on(dmsActions.setCampaignsByVin, (state, { campaignsByVin }) => {
        return {
            ...state,
            campaignsByVin: campaignsByVin,
        };
    }),
    on(dmsActions.hasLatestCampaignFetched, (state) => {
        return {
            ...state,
            hasLatestCampaignFetched: true,
        };
    }),
    on(dmsActions.hasCampaignHistoryFetched, (state) => {
        return {
            ...state,
            hasCampaignHistoryFetched: true,
        };
    }),
    on(dmsActions.isCampaignLoading, (state, { isLoading }) => {
        return {
            ...state,
            isCampaignLoading: isLoading,
        };
    }),
    on(dmsActions.isLatestCampaignLoading, (state, { isLoading }) => {
        return {
            ...state,
            isLatestCampaignLoading: isLoading,
        };
    }),
    on(dmsActions.setSort, (state, { sortBy, sortDirection }) => {
        return {
            ...state,
            sortState: {
                ...state.sortState,
                roTable: { sortBy: sortBy, sortDirection: sortDirection },
            },
        };
    })
);
